import Alert from 'core-system/Icons/Misc/Alert'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Radio from 'core-system/Radio/Radio'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { AppState } from 'redux/config/store'
import { employeeActions } from 'redux/employee/employeeSlice'
import { MyCommuter } from 'redux/employee/employeeTypes'
import employerService from 'redux/employer/employerService'
import ColorPill from 'core-system/Pill/ColorPill'
import styled from 'styled-components'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import AlternateButton from 'core-system/Button/AlternateButton'

const AlertIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
`

const EmployeeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
`

const EmployeeListBody = styled(ModalBody)`
  max-height: ${pxToRem(400)};
`

const DeliverySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem ${pxToRem(52)};
  border-top: 0.0625rem solid ${palette.grey.grey200};
`

const DeliveryOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  cursor: pointer;
`

type InviteDelivery = 'WITH_EMAIL' | 'WITHOUT_EMAIL'

interface InviteUsersModalProps {
  segmentId: string
  closeModal: () => void
  open: boolean
}

const InviteUsersModal = (props: InviteUsersModalProps) => {
  const { segmentId, closeModal, open } = props

  const unregisteredEmployees = useSelector(
    (state: AppState) => state.employee.myCommuters[segmentId]
  )
    ?.filter(
      (employee) =>
        employee.hasRegistered === false && employee.hasInvite === false
    )
    ?.sort((a, b) => a.name.localeCompare(b.name))

  const allUnselectedEmployees = useMemo(
    () =>
      unregisteredEmployees?.reduce((employees, employee) => {
        employees[employee.id] = false
        return employees
      }, {} as Record<string, boolean>),
    [unregisteredEmployees]
  )

  const dispatch = useDispatch()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectAll, setSelectAll] = useState(false)
  const [selectedEmployees, setSelectedEmployees] = useState<
    Record<string, boolean>
  >(allUnselectedEmployees || {})
  const [inviteDelivery, setInviteDelivery] =
    useState<InviteDelivery>('WITH_EMAIL')

  const sendWelcomeEmail = inviteDelivery === 'WITH_EMAIL'

  const selectedEmployeeIds = useMemo(
    () =>
      Object.keys(selectedEmployees).filter(
        (employeeId) => selectedEmployees[employeeId]
      ),
    [selectedEmployees]
  )
  const selectedCount = selectedEmployeeIds.length
  const isInviteDisabled = selectedCount === 0

  const inviteButtonLabel = (() => {
    if (selectedCount === 0) {
      return sendWelcomeEmail ? 'Invite & Send Emails' : 'Invite'
    }
    if (sendWelcomeEmail) {
      return selectedCount === 1
        ? 'Invite 1 & Send Email'
        : `Invite ${selectedCount} & Send Emails`
    }
    return selectedCount === 1 ? 'Invite 1' : `Invite ${selectedCount}`
  })()

  const toggleEmployee = (employeeId: string) => {
    setSelectedEmployees((prev) => ({
      ...prev,
      [employeeId]: !prev[employeeId],
    }))
  }

  const renderEmployeeSelect = () => {
    if (!unregisteredEmployees || unregisteredEmployees.length === 0) {
      return (
        <AlertIconContainer>
          <Text variant='h5' textColor={palette.text.secondary}>
            No Commuters to Invite
          </Text>
        </AlertIconContainer>
      )
    }
    return unregisteredEmployees.map((employee: MyCommuter) => (
      <EmployeeRow key={employee.id} onClick={() => toggleEmployee(employee.id)}>
        <Radio active={Boolean(selectedEmployees[employee.id])} />
        <Text variant='body1'>{employee.name}</Text>
        <ColorPill
          color='pink'
          text='Needs Invite'
        />
      </EmployeeRow>
    ))
  }

  const handleCloseModal = () => {
    setShowConfirmation(false)
    setSelectedEmployees(allUnselectedEmployees || {})
    setSelectAll(false)
    setInviteDelivery('WITH_EMAIL')
    closeModal()
    SegmentService.track('close-invite-employees-modal')
  }

  const handleEditExitModal = () => {
    if (showConfirmation) {
      setShowConfirmation(false)
      SegmentService.track('edit-selected-employees')
    } else {
      handleCloseModal()
    }
  }

  const handleInviteSubmit = useCallback(async () => {
    if (showConfirmation) {
      await employerService.rolloutUsers({
        employees: selectedEmployeeIds,
        send_welcome_email: sendWelcomeEmail,
      })

      SegmentService.track('user-rollout-submit', {
        employees: selectedEmployeeIds,
        send_welcome_email: sendWelcomeEmail,
      })
      setSelectedEmployees(allUnselectedEmployees || {})
      setShowConfirmation(false)
      setInviteDelivery('WITH_EMAIL')
      closeModal()

      dispatch(employeeActions.getMyCommuters({ segment: segmentId }))
    } else {
      setShowConfirmation(true)
      SegmentService.track('user-rollout-confirmation', {
        employees: selectedEmployeeIds,
        send_welcome_email: sendWelcomeEmail,
      })
    }
  }, [
    showConfirmation,
    allUnselectedEmployees,
    closeModal,
    dispatch,
    selectedEmployeeIds,
    segmentId,
    sendWelcomeEmail,
  ])

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees(allUnselectedEmployees || {})
    } else {
      setSelectedEmployees(
        unregisteredEmployees?.reduce((employees, employee) => {
          employees[employee.id] = true
          return employees
        }, {} as Record<string, boolean>) || {}
      )
    }
    setSelectAll(!selectAll)
  }

  return (
    <Modal
      open={open}
      onClose={handleCloseModal}
      width={pxToRem(514)}
      maxHeight={pxToRem(640)}
    >
      <ModalHeader
        title='Invite Commuters'
        style={{ textTransform: 'capitalize' }}
      />
      {showConfirmation ? (
        <ModalBody marginY='1rem'>
          <AlertIconContainer>
            <Alert
              color={palette.secondary.purple300}
              height={40}
              width={40}
              style={{ marginBottom: '1rem' }}
            />
          </AlertIconContainer>
          <Text variant='h5' marginBottom='0.75rem'>
            Invite {selectedCount}{' '}
            {selectedCount === 1 ? 'commuter' : 'commuters'}?
          </Text>
          <Text
            variant='body1'
            marginBottom='1rem'
            textColor={palette.text.secondary}
          >
            {sendWelcomeEmail
              ? 'They will be marked as Invited and receive an email invitation.'
              : 'They will be marked as Invited — no email will be sent.'}
          </Text>
          {unregisteredEmployees
            ?.filter((employee) => selectedEmployees[employee.id])
            ?.map((employee) => (
              <Text variant='body1' marginBottom='0.5rem' key={employee.id}>
                {`• ${employee.name}`}
              </Text>
            ))}
        </ModalBody>
      ) : (
        <>
          <EmployeeListBody marginY='1rem'>
            {unregisteredEmployees && unregisteredEmployees.length > 0 ? (
              <Text variant='h5' marginBottom='1rem'>
                Select commuters that you wish to invite:
              </Text>
            ) : null}
            {renderEmployeeSelect()}
          </EmployeeListBody>
          {unregisteredEmployees && unregisteredEmployees.length > 0 ? (
            <DeliverySection>
              <Text variant='h5'>How should they be invited?</Text>
              <DeliveryOption onClick={() => setInviteDelivery('WITH_EMAIL')}>
                <Radio
                  active={sendWelcomeEmail}
                  label='Invite with Email'
                />
                <Text
                  variant='body2'
                  textColor={palette.text.secondary}
                  marginLeft='1.875rem'
                >
                  Marks them as Invited and sends an email invitation.
                </Text>
              </DeliveryOption>
              <DeliveryOption
                onClick={() => setInviteDelivery('WITHOUT_EMAIL')}
              >
                <Radio
                  active={!sendWelcomeEmail}
                  label='Invite without Email'
                />
                <Text
                  variant='body2'
                  textColor={palette.text.secondary}
                  marginLeft='1.875rem'
                >
                  Marks them as Invited — no email is sent.
                </Text>
              </DeliveryOption>
            </DeliverySection>
          ) : null}
        </>
      )}
      <ModalFooter>
        <ButtonContainer>
          <AlternateButton
            onClick={handleEditExitModal}
            style={{ marginRight: 'auto' }}
          >
            {showConfirmation ? 'Edit' : 'Exit'}
          </AlternateButton>
          {!showConfirmation && (
            <SecondaryButton onClick={handleSelectAll}>
              {selectAll ? 'Deselect All' : 'Select All'}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={handleInviteSubmit}
            disabled={isInviteDisabled}
          >
            {showConfirmation ? 'Confirm Invite' : inviteButtonLabel}
          </PrimaryButton>
        </ButtonContainer>
      </ModalFooter>
    </Modal>
  )
}

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  InviteUsersModal.displayName = 'InviteUsersModal'
}

export default InviteUsersModal
